import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { salesService } from '../../services/salesService';
import * as XLSX from 'xlsx';
import {jsPDF} from 'jspdf';
import {autoTable} from 'jspdf-autotable';

const SalesOrders = () => {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allOrdersForChart, setAllOrdersForChart] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDates, setSyncDates] = useState({
    startDate: '',
    endDate: ''
  });

  const [showExportMenu, setShowExportMenu] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: '',
    tipo: '',
    status_lancamento: '',
    search: ''
  });

  // Listas únicas para os filtros de tipo e status_lancamento
  const [tipoOptions, setTipoOptions] = useState([]);
  const [statusLancamentoOptions, setStatusLancamentoOptions] = useState([]);

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch(statusLower) {
      case 'pendente':
        return { bgColor: '#fef3c7', textColor: '#92400e' };
      case 'aberto':
        return { bgColor: '#dbeafe', textColor: '#1e40af' };
      case 'aprovado':
        return { bgColor: '#dcfce7', textColor: '#166534' };
      case 'faturado':
        return { bgColor: '#d1fae5', textColor: '#065f46' };
      case 'entregue':
        return { bgColor: '#f0fdf4', textColor: '#14532d' };
      case 'cancelado':
        return { bgColor: '#fee2e2', textColor: '#991b1b' };
      default:
        return { bgColor: '#f3f4f6', textColor: '#374151' };
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    loadData(1, { key, direction });
  };

  const getSortedOrders = () => {
    return orders; // Ordenação feita no servidor
  };

  const SortableHeader = ({ label, sortKey, style }) => (
    <th 
      onClick={() => handleSort(sortKey)}
      style={{ 
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        transition: 'background-color 0.2s ease'
      }}
      onMouseOver={(e) => e.target.style.backgroundColor = '#f1f5f9'}
      onMouseOut={(e) => e.target.style.backgroundColor = '#f8fafc'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {label}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'asc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 4L4 0L8 4H0Z"/>
          </svg>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'desc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 0L4 4L8 0H0Z"/>
          </svg>
        </div>
      </div>
    </th>
  );

  const processChartData = (ordersData) => {
    if (!ordersData || ordersData.length === 0) {
      setChartData([]);
      return;
    }

    console.log('📊 Processando dados do gráfico...');
    console.log(`📊 Total de pedidos a processar: ${ordersData.length}`);

    const monthlyData = {};

    ordersData.forEach((order) => {
      const dateStr = order.order_date;
      const [year, month, day] = dateStr.split('-');
      const monthKey = `${year}-${month}`;

      const date = new Date(year, parseInt(month) - 1, parseInt(day));
      const monthName = date.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'short'
      }).replace(' de ', '/');

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthName,
          Pendente: 0,
          Aberto: 0,
          Aprovado: 0,
          Faturado: 0,
          Entregue: 0,
          Cancelado: 0
        };
      }

      const statusLower = order.status?.toLowerCase() || '';

      if (statusLower === 'pendente') {
        monthlyData[monthKey].Pendente++;
      } else if (statusLower === 'aberto') {
        monthlyData[monthKey].Aberto++;
      } else if (statusLower === 'aprovado') {
        monthlyData[monthKey].Aprovado++;
      } else if (statusLower === 'faturado') {
        monthlyData[monthKey].Faturado++;
      } else if (statusLower === 'entregue') {
        monthlyData[monthKey].Entregue++;
      } else if (statusLower === 'cancelado') {
        monthlyData[monthKey].Cancelado++;
      }
    });

    const chartArray = Object.keys(monthlyData)
      .sort()
      .map(key => monthlyData[key]);

    console.log('📊 Dados do gráfico processados:', chartArray);
    setChartData(chartArray);
  };

  const loadAllOrdersForChart = async (adjustedFilters) => {
    try {
      console.log('📈 Buscando pedidos para gráfico com filtros:', adjustedFilters);
      
      const ordersData = await salesService.getOrders(adjustedFilters, 1, 1000);
      
      console.log(`📈 Total de pedidos retornados para gráfico: ${ordersData.results?.length || 0}`);
      
      if (ordersData.results && ordersData.results.length > 0) {
        const dates = ordersData.results.map(o => o.order_date).sort();
        console.log('📅 Primeira data:', dates[0], '| Última data:', dates[dates.length - 1]);
      }
      
      return ordersData.results || [];
    } catch (error) {
      console.error('Erro ao carregar pedidos para gráfico:', error);
      if (error.message.includes('Sessão expirada')) {
        return [];
      }
      return [];
    }
  };

  const loadData = async (page = 1, customSort = null) => {
    try {
      setLoading(true);

      const currentSort = customSort || sortConfig;

      let adjustedFilters = {
        ...filters,
        sortBy: currentSort.key,
        sortDirection: currentSort.direction
      };

      const [summaryData, ordersData] = await Promise.all([
        salesService.getSummary(adjustedFilters),
        salesService.getOrders(adjustedFilters, page, 20)
      ]);
      
      console.log('📊 Summary recebido:', summaryData);
      console.log('📋 Pedidos da página recebidos:', ordersData.results?.length);
      
      setSummary(summaryData);
      setOrders(ordersData.results || []);
      setTotalPages(ordersData.total_pages || 1);
      setTotalOrders(ordersData.count || 0);
      setCurrentPage(page);

      const allOrders = await loadAllOrdersForChart(adjustedFilters);
      setAllOrdersForChart(allOrders);
      processChartData(allOrders);

      // Extrair opções únicas para filtros de tipo e status_lancamento
      const tipos = [...new Set(allOrders.map(o => o.tipo).filter(Boolean))].sort();
      const statusLanc = [...new Set(allOrders.map(o => o.status_lancamento).filter(Boolean))].sort();
      setTipoOptions(tipos);
      setStatusLancamentoOptions(statusLanc);
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      
      if (error.message.includes('Sessão expirada')) {
        return;
      }
      
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    console.log('🔍 Aplicando filtros:', filters);
    loadData(1);
  };

  const validateSyncDates = () => {
    const validation = salesService.validateSyncDates(syncDates.startDate, syncDates.endDate);
    if (!validation.valid) {
      alert(validation.message);
      return false;
    }
    return true;
  };

  const handleSync = async () => {
    if (!validateSyncDates()) {
      return;
    }

    try {
      setSyncing(true);
      setShowSyncModal(false);
      
      console.log('🔄 Iniciando sincronização...');
      console.log('📅 Período:', syncDates.startDate, 'a', syncDates.endDate);
      
      const result = await salesService.syncData({
        start_date: syncDates.startDate,
        end_date: syncDates.endDate
      });
      
      console.log('✅ Resultado da sincronização:', result);
      
      if (result && result.success) {
        // MENSAGEM ATUALIZADA COM DETALHES DE SKIPPED
        let message = `Sincronização concluída!\n\n` +
          `• ${result.total || 0} pedidos retornados pela API\n` +
          `• ${result.created || 0} pedidos criados\n`;
        
        if (result.deleted && result.deleted > 0) {
          message += `• ${result.deleted} pedidos antigos deletados\n`;
        }
        
        if (result.skipped && result.skipped > 0) {
          message += `• ${result.skipped} pedidos ignorados (data fora do período)\n`;
        }
        
        message += `\nPeríodo: ${syncDates.startDate} a ${syncDates.endDate}`;
        
        alert(message);
        
        const newFilters = {
          startDate: syncDates.startDate,
          endDate: syncDates.endDate,
          status: '',
          search: ''
        };
        
        setFilters(newFilters);
        loadData(1);
      } else {
        alert('Erro: ' + (result?.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('❌ Erro na sincronização:', error);
      
      if (error.message.includes('Sessão expirada')) {
        return;
      }
      
      alert('Erro: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (page) => {
    loadData(page);
  };

  const openSyncModal = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setSyncDates({ startDate, endDate });
    setShowSyncModal(true);
  };

  const exportToXLSX = () => {
    try {
      const sortedOrders = [...allOrdersForChart].sort((a, b) => {
        return new Date(b.order_date) - new Date(a.order_date);
      });

      const exportData = sortedOrders.map(order => ({
        'Número': order.number,
        'Cliente': order.client_name,
        'Status': order.status_display || order.status,
        'Tipo': order.tipo || '-',
        'Status Lançamento': order.status_lancamento || '-',
        'Data Emissão': (() => {
          const [year, month, day] = order.order_date.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date.toLocaleDateString('pt-BR');
        })(),
        'Valor': `R$ ${parseFloat(order.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Vendedor': order.salesperson || '-',
        'Itens': order.items_quantity || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 10 },
        { wch: 30 },
        { wch: 12 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 25 },
        { wch: 8 }
      ];
      ws['!cols'] = columnWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pedidos de Venda');

      const summaryData = [
        { 'Status': 'Pendente', 'Quantidade': summary?.pendente || 0, 'Valor Total': `R$ ${parseFloat(summary?.pendente_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'Em Aberto', 'Quantidade': summary?.aberto || 0, 'Valor Total': `R$ ${parseFloat(summary?.aberto_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'Aprovado', 'Quantidade': summary?.aprovado || 0, 'Valor Total': `R$ ${parseFloat(summary?.aprovado_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'Faturado', 'Quantidade': summary?.faturado || 0, 'Valor Total': `R$ ${parseFloat(summary?.faturado_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'Entregue', 'Quantidade': summary?.entregue || 0, 'Valor Total': `R$ ${parseFloat(summary?.entregue_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'Cancelado', 'Quantidade': summary?.cancelado || 0, 'Valor Total': `R$ ${parseFloat(summary?.cancelado_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
        { 'Status': 'TOTAL', 'Quantidade': summary?.total || 0, 'Valor Total': `R$ ${parseFloat(summary?.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

      const fileName = `Pedidos_venda_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setShowExportMenu(false);
    } catch (error) {
      alert('Erro ao exportar arquivo XLSX: ' + error.message);
    }
  };

  const formatCurrencyFull = (value) => {
    return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Relatório de Pedidos de Venda', 14, 20);

      doc.setFontSize(10);
      doc.text(`Período: ${filters.startDate} a ${filters.endDate}`, 14, 28);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      // Resumo por status
      doc.setFontSize(14);
      doc.text('Resumo por Status', 14, 45);

      const statusList = ['pendente', 'aberto', 'aprovado', 'faturado', 'entregue', 'cancelado'];
      const statusLabels = { pendente: 'Pendente', aberto: 'Em Aberto', aprovado: 'Aprovado', faturado: 'Faturado', entregue: 'Entregue', cancelado: 'Cancelado' };
      const summaryBody = statusList.map(st => [
        statusLabels[st] || st,
        summary?.[st] || 0,
        formatCurrencyFull(summary?.[st + '_value'] || 0)
      ]);
      summaryBody.push(['TOTAL', summary?.total || 0, formatCurrencyFull(summary?.total_value || 0)]);

      autoTable(doc, {
        startY: 50,
        head: [['Status', 'Quantidade', 'Valor Total']],
        body: summaryBody,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [22, 163, 74] }
      });

      // Listagem
      doc.setFontSize(14);
      const finalY = doc.lastAutoTable.finalY || 50;
      doc.text('Listagem de Pedidos', 14, finalY + 15);

      const sortedOrders = [...allOrdersForChart].sort((a, b) => new Date(b.order_date) - new Date(a.order_date));

      const tableData = sortedOrders.map(order => [
        order.number || '-',
        (order.client_name || '-').substring(0, 35),
        order.status_display || order.status || '-',
        order.tipo || '-',
        order.order_date ? (() => {
          const [year, month, day] = order.order_date.split('-');
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('pt-BR');
        })() : '-',
        formatCurrencyFull(order.total_value),
        order.salesperson || '-'
      ]);

      autoTable(doc, {
        startY: finalY + 20,
        head: [['Número', 'Cliente', 'Status', 'Tipo', 'Data Emissão', 'Valor Total', 'Vendedor']],
        body: tableData,
        theme: 'striped',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [22, 163, 74] },
        margin: { left: 14, right: 14 }
      });

      const fileName = `Pedidos_venda_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      setShowExportMenu(false);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('button')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  if (loading && orders.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #e5e7eb', 
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280', margin: 0 }}>Carregando pedidos...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '2rem', fontWeight: '700' }}>
            Pedidos de Venda
          </h1>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Referência: Data de Emissão | Status individuais disponíveis
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {/* Botão Exportar */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={allOrdersForChart.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: allOrdersForChart.length === 0 ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: allOrdersForChart.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar
            </button>

            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.5rem',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb',
                zIndex: 1000,
                minWidth: '200px',
                overflow: 'hidden'
              }}>
                <button
                  onClick={exportToXLSX}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #f1f5f9',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="20" height="20" fill="#10b981" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar XLSX</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Planilha Excel com todos os dados</div>
                  </div>
                </button>
                <button
                  onClick={exportToPDF}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar PDF</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Relatório PDF com resumo e listagem</div>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button
            onClick={openSyncModal}
            disabled={syncing}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: syncing ? '#9ca3af' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)'
            }}
          >
            {syncing ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Sincronizando...
              </>
            ) : (
              <>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar Pedidos
              </>
            )}
          </button>
        </div>
      </div>

      {!loading && orders.length === 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="24" height="24" fill="currentColor" style={{ color: '#0ea5e9', minWidth: '24px' }} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#0c4a6e', fontSize: '1.125rem' }}>
              Nenhum pedido encontrado
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
              Use "Sincronizar Pedidos" para importar dados da API M8
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Filtros de Visualização
          </h3>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          marginBottom: '2rem',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Emissão - Início
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange({ startDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Emissão - Fim
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange({ endDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange({ status: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              <option value="">Todos os Status</option>
              <option value="pendente">Pendente</option>
              <option value="aberto">Em Aberto</option>
              <option value="aprovado">Aprovado</option>
              <option value="faturado">Faturado</option>
              <option value="entregue">Entregue</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Tipo de Pedido
            </label>
            <select
              value={filters.tipo}
              onChange={(e) => handleFilterChange({ tipo: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              <option value="">Todos os Tipos</option>
              {tipoOptions.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Status Lançamento
            </label>
            <select
              value={filters.status_lancamento}
              onChange={(e) => handleFilterChange({ status_lancamento: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              <option value="">Todos</option>
              {statusLancamentoOptions.map((sl) => (
                <option key={sl} value={sl}>{sl}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Busca
            </label>
            <input
              type="text"
              placeholder="Pesquisar..."
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setFilters({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                status: '',
                tipo: '',
                status_lancamento: '',
                search: ''
              });
              setCurrentPage(1);
              applyFilters();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Limpar Filtros
          </button>
          <button
            onClick={applyFilters}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Aplicar Filtros
          </button>
        </div>
      </div>

      {/* Cards de Resumo por Status */}
      {summary && (
        <>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
            Resumo por Status
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {[
              {
                label: 'Pendente',
                count: summary.pendente || 0,
                value: summary.pendente_value || 0,
                color: '#f59e0b',
                bgColor: '#fef3c7',
                textColor: '#92400e'
              },
              {
                label: 'Em Aberto',
                count: summary.aberto || 0,
                value: summary.aberto_value || 0,
                color: '#3b82f6',
                bgColor: '#dbeafe',
                textColor: '#1e40af'
              },
              {
                label: 'Aprovado',
                count: summary.aprovado || 0,
                value: summary.aprovado_value || 0,
                color: '#22c55e',
                bgColor: '#dcfce7',
                textColor: '#166534'
              },
              {
                label: 'Faturado',
                count: summary.faturado || 0,
                value: summary.faturado_value || 0,
                color: '#10b981',
                bgColor: '#d1fae5',
                textColor: '#065f46'
              },
              {
                label: 'Entregue',
                count: summary.entregue || 0,
                value: summary.entregue_value || 0,
                color: '#059669',
                bgColor: '#f0fdf4',
                textColor: '#14532d'
              },
              {
                label: 'Cancelado',
                count: summary.cancelado || 0,
                value: summary.cancelado_value || 0,
                color: '#dc2626',
                bgColor: '#fee2e2',
                textColor: '#991b1b'
              },
              {
                label: 'Total',
                count: summary.total || 0,
                value: summary.total_value || 0,
                color: '#6b7280',
                bgColor: '#f3f4f6',
                textColor: '#374151'
              }
            ].map((item, index) => (
              <div key={index} style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                border: '1px solid #f1f5f9',
                transition: 'transform 0.2s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: item.textColor, fontSize: '0.875rem', fontWeight: '600' }}>{item.label}</h3>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: item.color,
                    borderRadius: '50%'
                  }}></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#1f2937', lineHeight: '1' }}>
                      R$ {parseFloat(item.value || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>valor total</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#FFC000' }}>
                      R$ {item.count > 0
                        ? (parseFloat(item.value || 0) / item.count).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })
                        : '0,00'}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#FFC000', margin: '0.25rem 0 0 0' }}>ticket médio</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: item.textColor }}>
                      {item.count}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>pedidos</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Gráfico */}
      {chartData.length > 0 && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid #f1f5f9',
          marginBottom: '2rem'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
            Pedidos por Mês (Data de Emissão)
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '400', marginLeft: '1rem' }}>
              Total: {allOrdersForChart.length} pedidos em {chartData.length} {chartData.length === 1 ? 'mês' : 'meses'}
            </span>
          </h3>
          <div style={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                  domain={[0, 'dataMax + 5']}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Bar dataKey="Pendente" fill="#f59e0b" name="Pendente" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Pendente" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
                <Bar dataKey="Aberto" fill="#3b82f6" name="Em Aberto" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Aberto" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
                <Bar dataKey="Aprovado" fill="#22c55e" name="Aprovado" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Aprovado" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
                <Bar dataKey="Faturado" fill="#10b981" name="Faturado" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Faturado" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
                <Bar dataKey="Entregue" fill="#059669" name="Entregue" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Entregue" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
                <Bar dataKey="Cancelado" fill="#dc2626" name="Cancelado" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Cancelado" position="top" style={{fontSize: '10px', fill: '#6b7280'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: '#166534'
          }}>
            ✅ Gráfico baseado em {allOrdersForChart.length} pedidos | Agrupados por mês de emissão
          </div>
        </div>
      )}
      
      {/* Tabela */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#fafbfc'
        }}>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Listagem de Pedidos
          </h2>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '0.875rem',
            backgroundColor: '#f3f4f6',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500'
          }}>
            Total: {totalOrders} | Página {currentPage} de {totalPages}
          </span>
        </div>
        
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              border: '3px solid #e5e7eb', 
              borderTop: '3px solid #16a34a',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#6b7280', margin: 0 }}>Carregando...</p>
          </div>
        ) : orders.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <SortableHeader label="Número" sortKey="number" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Cliente" sortKey="client_name" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Status" sortKey="status" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Tipo" sortKey="tipo" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Status Lanç." sortKey="status_lancamento" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Data Emissão" sortKey="order_date" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Valor Total" sortKey="total_value" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                    <SortableHeader label="Vendedor" sortKey="salesperson" style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }} />
                  </tr>
                </thead>
                <tbody>
                  {getSortedOrders().map((order) => {
                    const statusColors = getStatusColor(order.status);
                    return (
                      <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                          {order.number}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {order.client_name}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <span style={{
                            padding: '0.375rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: statusColors.bgColor,
                            color: statusColors.textColor
                          }}>
                            {order.status_display || order.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          {order.tipo || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          {order.status_lancamento || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {(() => {
                            const [year, month, day] = order.order_date.split('-');
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            return date.toLocaleDateString('pt-BR');
                          })()}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          R$ {parseFloat(order.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {order.salesperson || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ 
                padding: '1.5rem', 
                borderTop: '1px solid #f1f5f9', 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '0.75rem',
                backgroundColor: '#fafbfc'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  ← Anterior
                </button>

                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '0.75rem 1rem',
                        border: `2px solid ${pageNum === currentPage ? '#16a34a' : '#e5e7eb'}`,
                        backgroundColor: pageNum === currentPage ? '#16a34a' : 'white',
                        color: pageNum === currentPage ? 'white' : '#374151',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        minWidth: '44px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6b7280' }}>
            <svg style={{ width: '64px', height: '64px', margin: '0 auto 1.5rem', color: '#d1d5db' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
              Nenhum pedido encontrado
            </h3>
            <p style={{ margin: 0 }}>
              Ajuste os filtros ou sincronize novos dados
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
              Sincronizar Pedidos
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                Data Inicial:
              </label>
              <input
                type="date"
                value={syncDates.startDate}
                onChange={(e) => setSyncDates({...syncDates, startDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                Data Final:
              </label>
              <input
                type="date"
                value={syncDates.endDate}
                onChange={(e) => setSyncDates({...syncDates, endDate: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSyncModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSync}
                disabled={!syncDates.startDate || !syncDates.endDate}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (!syncDates.startDate || !syncDates.endDate) ? '#9ca3af' : '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!syncDates.startDate || !syncDates.endDate) ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Sincronizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrders;